/**
 * נורמליזציה ואימות של מספרי טלפון ישראליים → E.164
 *
 * ⚠️ רכיב גנרי בבעלות Ariana-Prj — סעיף 13.2. ראו shared/README.md
 *
 * 🔴 למה זה קריטי (TASKS — סיכון #3):
 *    זיהוי לקוח קבוע מתבצע לפי `phone_e164` בלבד. אם הנורמליזציה נכשלת בשקט,
 *    לקוח קבוע מקבל מחיר מלא במקום המחיר המיוחד שלו — ⛔ והוא לא מתלונן,
 *    הוא פשוט קונה במקום אחר.
 *
 * ⛔ חיפוש במסד תמיד לפי phone_e164. ⛔ לעולם לא לפי phone_raw.
 */
/**
 * תווי כיווניות של Unicode — נדבקים בהדבקה מוואטסאפ ומ-Word ושוברים כל regex.
 *
 * ⚠️ נכתבים כ-escape sequences בכוונה. התווים עצמם **בלתי נראים בקוד** —
 *    הדבקתם כפשוטם מייצרת קובץ מקור שאי אפשר לקרוא או לבדוק בעין,
 *    ו-ESLint פוסל אותם (no-irregular-whitespace).
 *
 * U+200E/200F סימני כיוון · U+202A–202E עיצוב דו-כיווני
 * U+2066–2069 בידוד דו-כיווני · U+061C סימן כיוון ערבי
 */
const BIDI_CONTROL_CHARS = /[\u200E\u200F\u202A-\u202E\u2066-\u2069\u061C]/g;
/**
 * רווחים "בלתי נראים" — NBSP, רווח צר, מפריד אפס-רוחב, BOM.
 * ⚠️ גם כאן escape sequences בלבד, מאותה סיבה.
 */
const INVISIBLE_SPACES = /[\u00A0\u2000-\u200B\u202F\u205F\u3000\uFEFF]/g;
const COUNTRY_CODE = '972';
/**
 * קידומות ישראליות תקפות והאורך הלאומי הנדרש להן (כולל ה-0 המוביל).
 *
 * ⚠️ הרשימה מכוונת: מוסיפים אליה רק קידומת שאומתה מול משרד התקשורת.
 * קידומת שאינה כאן ⛔ נדחית — עדיף לדחות מספר תקין נדיר מאשר לקבל שגוי.
 */
const PREFIXES = [
    // סלולר — 05X, סה"כ 10 ספרות (0501234567)
    { prefix: '050', nationalLength: 10, kind: 'mobile' },
    { prefix: '051', nationalLength: 10, kind: 'mobile' },
    { prefix: '052', nationalLength: 10, kind: 'mobile' },
    { prefix: '053', nationalLength: 10, kind: 'mobile' },
    { prefix: '054', nationalLength: 10, kind: 'mobile' },
    { prefix: '055', nationalLength: 10, kind: 'mobile' },
    { prefix: '056', nationalLength: 10, kind: 'mobile' },
    { prefix: '057', nationalLength: 10, kind: 'mobile' },
    { prefix: '058', nationalLength: 10, kind: 'mobile' },
    { prefix: '059', nationalLength: 10, kind: 'mobile' },
    // VoIP — 07X, סה"כ 10 ספרות
    { prefix: '072', nationalLength: 10, kind: 'voip' },
    { prefix: '073', nationalLength: 10, kind: 'voip' },
    { prefix: '074', nationalLength: 10, kind: 'voip' },
    { prefix: '076', nationalLength: 10, kind: 'voip' },
    { prefix: '077', nationalLength: 10, kind: 'voip' },
    { prefix: '078', nationalLength: 10, kind: 'voip' },
    // קווי — סה"כ 9 ספרות (031234567)
    { prefix: '02', nationalLength: 9, kind: 'landline' },
    { prefix: '03', nationalLength: 9, kind: 'landline' },
    { prefix: '04', nationalLength: 9, kind: 'landline' },
    { prefix: '08', nationalLength: 9, kind: 'landline' },
    { prefix: '09', nationalLength: 9, kind: 'landline' },
];
const ERROR_MESSAGES = {
    EMPTY: 'נא להזין מספר טלפון.',
    NO_DIGITS: 'המספר שהוזן אינו מכיל ספרות. לדוגמה: 050-123-4567',
    TOO_SHORT: 'המספר קצר מדי. מספר ישראלי תקין נראה כך: 050-123-4567',
    TOO_LONG: 'המספר ארוך מדי. מספר ישראלי תקין נראה כך: 050-123-4567',
    UNKNOWN_PREFIX: 'הקידומת אינה מוכרת. יש להזין מספר ישראלי, לדוגמה: 050-123-4567',
};
/**
 * ממיר קלט חופשי למספר ישראלי בפורמט E.164.
 *
 * מקבל, בין השאר:
 *   0501234567 · 050-123-4567 · 050 123 4567 · 050.123.4567
 *   +972501234567 · +972-50-123-4567 · 972501234567 · 00972501234567
 *   (050) 123-4567 · מספר עם תווי כיווניות מהדבקה
 */
export function normalizePhone(input) {
    if (input === null || input === undefined)
        return fail('EMPTY');
    // 1. ניקוי: תווי כיווניות, רווחים בלתי נראים, ואז כל מה שאינו ספרה או +
    const cleaned = input
        .replace(BIDI_CONTROL_CHARS, '')
        .replace(INVISIBLE_SPACES, ' ')
        .trim()
        .replace(/[^\d+]/g, '');
    if (cleaned === '')
        return fail(input.trim() === '' ? 'EMPTY' : 'NO_DIGITS');
    // 2. תיאום הקידומת הבינלאומית → מספר לאומי עם 0 מוביל
    let national;
    if (cleaned.startsWith('+' + COUNTRY_CODE)) {
        national = '0' + cleaned.slice(1 + COUNTRY_CODE.length);
    }
    else if (cleaned.startsWith('00' + COUNTRY_CODE)) {
        national = '0' + cleaned.slice(2 + COUNTRY_CODE.length);
    }
    else if (cleaned.startsWith(COUNTRY_CODE) && !cleaned.startsWith('0')) {
        // 972501234567 — בלי + ובלי 00
        national = '0' + cleaned.slice(COUNTRY_CODE.length);
    }
    else if (cleaned.startsWith('+')) {
        // ⛔ קידומת בינלאומית של מדינה אחרת — לא נתמך במערכת הזו
        return fail('UNKNOWN_PREFIX');
    }
    else {
        national = cleaned;
    }
    // ⚠️ ניקוי אפסים כפולים שנוצרים מ-"+972 050 …" — טעות הקלדה נפוצה
    national = national.replace(/^0{2,}/, '0');
    if (!/^\d+$/.test(national))
        return fail('UNKNOWN_PREFIX');
    // 3. התאמה לקידומת מוכרת. ⭐ הארוכה קודם — 050 לפני 05
    const match = [...PREFIXES]
        .sort((a, b) => b.prefix.length - a.prefix.length)
        .find((p) => national.startsWith(p.prefix));
    if (!match) {
        // אבחון מדויק יותר לפני ההודעה
        if (national.length < 9)
            return fail('TOO_SHORT');
        return fail('UNKNOWN_PREFIX');
    }
    if (national.length < match.nationalLength)
        return fail('TOO_SHORT');
    if (national.length > match.nationalLength)
        return fail('TOO_LONG');
    const withoutLeadingZero = national.slice(1);
    const e164 = `+${COUNTRY_CODE}${withoutLeadingZero}`;
    return {
        ok: true,
        e164,
        display: formatIsraeliPhone(national, match.kind),
        wa: `${COUNTRY_CODE}${withoutLeadingZero}`,
        kind: match.kind,
    };
}
/**
 * עוזר קצר לשימוש בשאילתות — מחזיר E.164 או `null`.
 * ⚠️ ⛔ לא לשימוש בטופס: הוא בולע את סיבת הכישלון ולכן אין מה להציג למשתמש.
 */
export function toE164OrNull(input) {
    const result = normalizePhone(input);
    return result.ok ? result.e164 : null;
}
/**
 * עיצוב לתצוגה. `dir="ltr"` חובה על האלמנט שמציג את התוצאה —
 * ⚠️ בלעדיו הדפדפן מהפך את סדר הקבוצות בהקשר עברי.
 */
export function formatIsraeliPhone(national, kind) {
    if (kind === 'landline') {
        // 031234567 → 03-123-4567
        return `${national.slice(0, 2)}-${national.slice(2, 5)}-${national.slice(5)}`;
    }
    // 0501234567 → 050-123-4567
    return `${national.slice(0, 3)}-${national.slice(3, 6)}-${national.slice(6)}`;
}
function fail(code) {
    return { ok: false, code, message: ERROR_MESSAGES[code] };
}
//# sourceMappingURL=normalizePhone.js.map